// ============================================
// Sub-Account Detail Page
// ============================================

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { subAccountApi } from '../utils/api';
import { Loader } from '../components/Loader';
import { cn, formatRelativeTime } from '../components/ui';
import {
  Bot,
  MessageSquare,
  CheckCircle2,
  Settings,
  Play,
  Pause,
  Edit,
  Trash2,
  ChevronDown,
  RefreshCw,
} from 'lucide-react';
import type { SubAccountDetail, SubAccountStats, GHLConversation, GHLMessage } from '../types';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Bot },
  { id: 'conversations', label: 'Conversations', icon: MessageSquare },
  { id: 'config', label: 'Configuration', icon: Settings },
] as const;

export function SubAccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'overview' | 'conversations' | 'config'>('overview');
  const [subAccount, setSubAccount] = useState<SubAccountDetail | null>(null);
  const [stats, setStats] = useState<SubAccountStats | null>(null);
  const [conversations, setConversations] = useState<GHLConversation[]>([]);
  const [conversationTotal, setConversationTotal] = useState(0);
  const [convLoading, setConvLoading] = useState(false);
  const [convError, setConvError] = useState<string | null>(null);
  const [convSearch, setConvSearch] = useState('');
  const [expandedConv, setExpandedConv] = useState<string | null>(null);
  const [messagesByConversation, setMessagesByConversation] = useState<Record<string, GHLMessage[]>>({});
  const [messagesLoading, setMessagesLoading] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (id) {
      loadSubAccount();
      loadStats();
    }
  }, [id]);

  useEffect(() => {
    if (id && activeTab === 'conversations') loadConversations();
  }, [id, activeTab]);

  const loadSubAccount = async () => {
    try {
      const response = await subAccountApi.getDetail(id!);
      setSubAccount(response.data);
    } catch (err: any) {
      console.error('Failed to load sub-account:', err);
    }
  };

  const loadStats = async () => {
    try {
      const response = await subAccountApi.getStats(id!);
      setStats(response.data);
    } catch {
      // Silent fail for stats
    }
  };

  const loadConversations = async () => {
    setConvLoading(true);
    setConvError(null);
    try {
      const response = await subAccountApi.getGhlConversations(id!, convSearch);
      setConversations(response.data.conversations);
      setConversationTotal(response.data.total);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setConvError(typeof detail === 'string' ? detail : 'Could not load this client’s GoHighLevel inbox');
      setConversations([]);
    } finally {
      setConvLoading(false);
    }
  };

  const loadMessages = async (conversationId: string) => {
    setMessagesLoading(current => ({ ...current, [conversationId]: true }));
    setConvError(null);
    try {
      const response = await subAccountApi.getGhlMessages(id!, conversationId);
      setMessagesByConversation(current => ({ ...current, [conversationId]: response.data }));
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setConvError(typeof detail === 'string' ? detail : 'Could not load this conversation’s messages');
    } finally {
      setMessagesLoading(current => ({ ...current, [conversationId]: false }));
    }
  };

  const handleBotToggle = async (activate: boolean) => {
    if (!subAccount) return;
    try {
      if (activate) {
        await subAccountApi.activateBot(id!);
      } else {
        await subAccountApi.deactivateBot(id!);
      }
      loadSubAccount();
      loadStats();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to toggle bot');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this sub-account? This action cannot be undone.')) return;
    try {
      await subAccountApi.delete(id!);
      window.location.href = '/';
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to delete sub-account');
    }
  };

  if (!subAccount) return <Loader fullScreen />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Link to="/" className="text-sm text-muted-foreground hover:underline mb-2 inline-block">
            ← Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold">{subAccount.name}</h1>
          <p className="text-muted-foreground">{subAccount.slug}</p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/sub-account/${id}/edit`}
            className="px-4 py-2 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 flex items-center gap-2"
          >
            <Edit className="h-4 w-4" />
            Edit
          </Link>
          {subAccount.is_bot_active ? (
            <button
              onClick={() => handleBotToggle(false)}
              className="px-4 py-2 bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 rounded-lg hover:bg-yellow-200 dark:hover:bg-yellow-900/50 flex items-center gap-2"
            >
              <Pause className="h-4 w-4" />
              Pause Bot
            </button>
          ) : (
            <button
              onClick={() => handleBotToggle(true)}
              className="px-4 py-2 bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 flex items-center gap-2"
            >
              <Play className="h-4 w-4" />
              Start Bot
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Conversations</p>
              <p className="text-3xl font-bold">{stats?.total_conversations || 0}</p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <MessageSquare className="h-6 w-6 text-blue-500" />
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Active Now</p>
              <p className="text-3xl font-bold text-green-600 dark:text-green-400">{stats?.active_conversations || 0}</p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Bot className="h-6 w-6 text-green-500" />
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">{stats?.completed_conversations || 0}</p>
            </div>
            <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
              <CheckCircle2 className="h-6 w-6 text-emerald-500" />
            </div>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Forms Verified</p>
              <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{stats?.form_verified_conversations || 0}</p>
            </div>
            <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <CheckCircle2 className="h-6 w-6 text-purple-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="border-b border-border">
          <nav className="flex gap-1 p-1" role="tablist">
            {TABS.map(({ id: tabId, label, icon: Icon }) => (
              <button
                key={tabId}
                role="tab"
                aria-selected={activeTab === tabId}
                onClick={() => setActiveTab(tabId as typeof activeTab)}
                className={cn(
                  "flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                  activeTab === tabId
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Panels */}
        <div className="p-4">
          {activeTab === 'overview' && (
            <OverviewTab subAccount={subAccount} />
          )}
          {activeTab === 'conversations' && (
            <ConversationsTab
              clientName={subAccount.name}
              conversations={conversations}
              total={conversationTotal}
              loading={convLoading}
              error={convError}
              search={convSearch}
              setSearch={setConvSearch}
              expandedConv={expandedConv}
              setExpandedConv={setExpandedConv}
              messagesByConversation={messagesByConversation}
              messagesLoading={messagesLoading}
              onLoadMessages={loadMessages}
              onRefresh={loadConversations}
            />
          )}
          {activeTab === 'config' && (
            <ConfigTab subAccount={subAccount} onDelete={handleDelete} />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Overview Tab
// ============================================
function OverviewTab({ subAccount }: { subAccount: SubAccountDetail }) {
  return (
    <div className="space-y-6">
      {/* Credentials Status */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="bg-card border border-border rounded-lg p-4">
          <h3 className="font-medium mb-3 flex items-center gap-2">
            <Bot className="h-4 w-4" /> GHL (GoHighLevel)
          </h3>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Location ID</span>
              <span className={cn("font-medium", subAccount.has_ghl_credentials ? 'text-green-600' : 'text-red-600')}>
                {subAccount.has_ghl_credentials ? 'Configured' : 'Missing'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">API Key</span>
              <span className={cn("font-medium", subAccount.has_ghl_credentials ? 'text-green-600' : 'text-red-600')}>
                {subAccount.has_ghl_credentials ? 'Configured' : 'Missing'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">PIT</span>
              <span className={cn("font-medium", subAccount.ghl_pit ? 'text-green-600' : 'text-red-600')}>
                {subAccount.ghl_pit ? 'Configured' : 'Missing'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Form ID</span>
              <span className="font-medium font-mono text-xs">{subAccount.ghl_form_id || 'Not set'}</span>
            </div>
          </dl>
        </div>
      </div>

      {/* Bot Configuration Summary */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="font-medium mb-3">Bot Configuration</h3>
        <dl className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 text-sm">
          <div>
            <span className="text-muted-foreground">Lead Context</span>
            <p className="font-medium truncate">{subAccount.lead_context}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Form URL</span>
            <p className="font-medium truncate">{subAccount.form_url || 'Not set'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Timeout</span>
            <p className="font-medium">{subAccount.lead_timeout_minutes} minutes</p>
          </div>
          <div>
            <span className="text-muted-foreground">Max Messages/Run</span>
            <p className="font-medium">{subAccount.max_messages_per_run}</p>
          </div>
          <div className="md:col-span-2">
            <span className="text-muted-foreground">Qualification Questions</span>
            <ol className="list-decimal list-inside mt-1 space-y-1 text-xs text-muted-foreground">
              <li>{subAccount.qual_question_1}</li>
              <li>{subAccount.qual_question_2}</li>
              <li>{subAccount.qual_question_3}</li>
              <li>{subAccount.qual_question_4}</li>
            </ol>
          </div>
          <div className="md:col-span-2">
            <span className="text-muted-foreground">Agent goal</span>
            <p className="font-medium text-sm mt-1">{subAccount.agent_goal || 'Not set'}</p>
          </div>
          <div className="md:col-span-2">
            <span className="text-muted-foreground">Behavior rules</span>
            <p className="font-medium text-sm mt-1 line-clamp-3">{subAccount.agent_behavior || 'Not set'}</p>
          </div>
          <div className="md:col-span-2">
            <span className="text-muted-foreground">Business knowledge base</span>
            <p className="font-medium text-sm mt-1 line-clamp-3">{subAccount.bot_context || 'No knowledge added yet'}</p>
          </div>
        </dl>
      </div>

      {/* Recent Activity */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h3 className="font-medium mb-3">Recent Bot Runs</h3>
        <p className="text-sm text-muted-foreground">Bot run history will appear here once the bot is active.</p>
      </div>
    </div>
  );
}

// ============================================
// Conversations Tab — live inbox for this client's own GHL location
// ============================================
function ConversationsTab({
  clientName,
  conversations,
  total,
  loading,
  error,
  search,
  setSearch,
  expandedConv,
  setExpandedConv,
  messagesByConversation,
  messagesLoading,
  onLoadMessages,
  onRefresh,
}: {
  clientName: string;
  conversations: GHLConversation[];
  total: number;
  loading: boolean;
  error: string | null;
  search: string;
  setSearch: (value: string) => void;
  expandedConv: string | null;
  setExpandedConv: (value: string | null) => void;
  messagesByConversation: Record<string, GHLMessage[]>;
  messagesLoading: Record<string, boolean>;
  onLoadMessages: (id: string) => void;
  onRefresh: () => void;
}) {
  const handleRowClick = (conversationId: string) => {
    if (expandedConv === conversationId) {
      setExpandedConv(null);
    } else {
      setExpandedConv(conversationId);
      if (!messagesByConversation[conversationId]) onLoadMessages(conversationId);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="font-semibold">{clientName} inbox</h2>
          <p className="text-sm text-muted-foreground">Live conversations and message history from this client’s GoHighLevel location.</p>
        </div>
        <span className="text-xs text-muted-foreground">Showing {conversations.length} recent conversations{total > conversations.length ? ` of ${total.toLocaleString()}` : ''}</span>
      </div>
      <form className="flex flex-col gap-3 sm:flex-row" onSubmit={(event) => { event.preventDefault(); onRefresh(); }}>
        <input
          type="search"
          placeholder="Search this client’s contacts..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className="min-w-0 flex-1 rounded-lg border border-input bg-background px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
        />
        <button type="submit" disabled={loading} className="rounded-lg bg-secondary px-4 py-2 text-secondary-foreground hover:bg-secondary/80">Search</button>
        <button type="button" onClick={onRefresh} disabled={loading} className="flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-primary-foreground hover:bg-primary/90">
          <RefreshCw className={cn('h-4 w-4', loading && 'animate-spin')} /> Refresh inbox
        </button>
      </form>
      {error && <div role="alert" className="rounded-lg border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950/30 dark:text-rose-200">{error}</div>}
      {loading ? <div className="py-8 text-center"><Loader /></div> : conversations.length === 0 ? (
        <div className="py-12 text-center">
          <MessageSquare className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <h3 className="mb-1 text-lg font-medium">No conversations found</h3>
          <p className="text-muted-foreground">{error ? 'Check this client’s GHL connection and try again.' : 'There are no recent conversations in this client’s GoHighLevel inbox.'}</p>
        </div>
      ) : <div className="space-y-2">
        {conversations.map(conv => (
          <GHLConversationRow
            key={conv.id}
            conv={conv}
            isExpanded={expandedConv === conv.id}
            messages={messagesByConversation[conv.id]}
            messagesLoading={Boolean(messagesLoading[conv.id])}
            onClick={() => handleRowClick(conv.id)}
          />
        ))}
      </div>}
    </div>
  );
}

function GHLConversationRow({
  conv,
  isExpanded,
  messages,
  messagesLoading,
  onClick,
}: {
  conv: GHLConversation;
  isExpanded: boolean;
  messages?: GHLMessage[];
  messagesLoading: boolean;
  onClick: () => void;
}) {
  const orderedMessages = [...(messages ?? [])].sort((a, b) => {
    const first = a.date_added ? Date.parse(a.date_added) : 0;
    const second = b.date_added ? Date.parse(b.date_added) : 0;
    return first - second;
  });
  return (
    <article className="overflow-hidden rounded-xl border border-border bg-card">
      <button onClick={onClick} aria-expanded={isExpanded} className="flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-muted/40 sm:items-center">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-primary/10 text-sm font-semibold text-primary">{conv.contact_name.slice(0, 1).toUpperCase()}</div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-medium">{conv.contact_name}</p>
            <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{conv.channel}</span>
            {conv.unread_count > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">{conv.unread_count} unread</span>}
          </div>
          <p className="mt-1 truncate text-sm text-muted-foreground">{conv.last_message || 'No text preview available'}</p>
          <p className="mt-1 text-xs text-muted-foreground">{conv.contact_email || conv.contact_phone || `Contact ${conv.contact_id || ''}`}</p>
        </div>
        <div className="hidden shrink-0 items-end gap-1 text-right sm:flex sm:flex-col">
          <span className={cn('rounded-full px-2 py-1 text-[11px] font-medium', conv.last_message_direction === 'inbound' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300')}>{conv.last_message_direction === 'inbound' ? 'Contact replied' : conv.last_message_action === 'automated' ? 'Automated outbound' : 'Business outbound'}</span>
          <span className="text-xs text-muted-foreground">{formatRelativeTime(conv.last_message_at)}</span>
        </div>
        <ChevronDown className={cn('mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform sm:mt-0', isExpanded && 'rotate-180')} />
      </button>
      {isExpanded && <div className="space-y-3 border-t border-border bg-muted/20 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div><p className="text-sm font-semibold">Message history</p><p className="text-xs text-muted-foreground">Inbound and outbound messages as recorded in GoHighLevel.</p></div>
          <span className="text-xs text-muted-foreground">Conversation status: {conv.status}</span>
        </div>
        {messagesLoading ? <div className="py-6 text-center"><Loader /></div> : orderedMessages.length ? (
          <div className="max-h-[32rem] space-y-3 overflow-y-auto rounded-xl border border-border/70 bg-background p-3 sm:p-4">
            {orderedMessages.map(message => {
              const outbound = message.direction === 'outbound';
              return <div key={message.id} className={cn('flex', outbound ? 'justify-end' : 'justify-start')}>
                <div className={cn('max-w-[88%] rounded-2xl px-4 py-3 sm:max-w-[75%]', outbound ? 'rounded-br-md bg-primary text-primary-foreground' : 'rounded-bl-md bg-muted')}>
                  <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] opacity-75">
                    <span className="font-semibold uppercase">{outbound ? 'Outbound · business / agent' : 'Inbound · contact'}</span>
                    {message.source && <span>via {message.source}</span>}
                    {message.status && <span>· {message.status}</span>}
                  </div>
                  <p className="whitespace-pre-wrap break-words text-sm">{message.body || (message.attachments.length ? `[${message.attachments.length} attachment(s)]` : '[No text content]')}</p>
                  {message.error && <p className="mt-2 text-xs font-medium">Delivery issue: {message.error}</p>}
                  <p className="mt-2 text-right text-[10px] opacity-70">{formatRelativeTime(message.date_added)}</p>
                </div>
              </div>;
            })}
          </div>
        ) : <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">No message history returned for this conversation.</p>}
      </div>}
    </article>
  );
}

// ============================================
// Config Tab
// ============================================
function ConfigTab({ subAccount, onDelete }: { subAccount: SubAccountDetail; onDelete: () => void }) {
  return (
    <div className="space-y-6">
      {/* GHL Credentials */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-medium mb-4 flex items-center gap-2">
          <Bot className="h-5 w-5" /> GHL Credentials
        </h3>
        <dl className="grid gap-4 md:grid-cols-2 text-sm">
          <div>
            <span className="text-muted-foreground">Location ID</span>
            <p className="font-medium font-mono text-xs mt-1">{subAccount.ghl_location_id || 'Not set'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Form ID</span>
            <p className="font-medium font-mono text-xs mt-1">{subAccount.ghl_form_id || 'Not set'}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Form Submitted Field</span>
            <p className="font-medium font-mono text-xs mt-1">{subAccount.ghl_custom_field_form_submitted}</p>
          </div>
          <div>
            <span className="text-muted-foreground">IG Username Field</span>
            <p className="font-medium font-mono text-xs mt-1">{subAccount.ghl_custom_field_ig_username}</p>
          </div>
        </dl>
      </div>

      {/* Bot Configuration */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-medium mb-4">Bot Behavior Settings</h3>
        <dl className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 text-sm">
          <div>
            <span className="text-muted-foreground">Lead Timeout</span>
            <p className="font-medium mt-1">{subAccount.lead_timeout_minutes} minutes</p>
          </div>
          <div>
            <span className="text-muted-foreground">Max Messages/Run</span>
            <p className="font-medium mt-1">{subAccount.max_messages_per_run}</p>
          </div>
          <div>
            <span className="text-muted-foreground">Bot Status</span>
            <p className="font-medium mt-1">{subAccount.is_bot_active ? 'Active' : 'Inactive'}</p>
          </div>
        </dl>
      </div>

      {/* Danger Zone */}
      <div className="bg-card border border-red-200 dark:border-red-800 rounded-lg p-6">
        <h3 className="font-medium mb-4 text-red-600 dark:text-red-400 flex items-center gap-2">
          <Trash2 className="h-5 w-5" /> Danger Zone
        </h3>
        <p className="text-sm text-muted-foreground mb-4">
          Deleting this sub-account will permanently remove all conversations, messages, and configuration.
          This action cannot be undone.
        </p>
        <button
          onClick={onDelete}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
        >
          <Trash2 className="h-4 w-4" />
          Delete Sub-Account
        </button>
      </div>
    </div>
  );
}